/**
 * OPFS 内部工作区：工作文档的自动保存镜像与崩溃恢复（§2.6：
 * 「工作文档（.cvsm）始终镜像一份到 OPFS，做崩溃恢复」）。
 *
 * 它也实现 FileGateway——`save()` 就是"镜像一份"，`open()` 就是"取回镜像"，
 * 三种实现因此能共用同一套接口测试。用户看不到这个工作区，
 * 调度镜像的时机（防抖、命令后触发）是应用层的事，这里只提供可靠的读写。
 *
 * 崩溃安全的关键约定：
 * - 每份文档两个文件：`<key>.bin`（数据）与 `<key>.meta.json`（名字、大小、时间）。
 * - **先写数据、后写 meta**。OPFS 的 createWritable 在 close 时原子替换，
 *   单个文件不会出现半截；跨两个文件的窗口期靠 meta 里记录的大小兜底——
 *   恢复时大小对不上就标记 `intact: false`，字节照给，由用户决定要不要。
 * - 文件名是文档名的 base64url（文档名来自用户，可能含 `/`、`..` 等
 *   路径穿越素材，CLAUDE.md 规则 10），原名存在 meta 里。
 */
import {
  throwIfAborted,
  type FileGateway,
  type FileHandleRef,
  type GatewayCallOptions,
  type OpenedFile,
  type SavePayload,
} from './types';

/** OPFS 目录句柄的最小面。真实实现与假实现都满足它。 */
export interface WorkspaceDirLike {
  getFileHandle(name: string, options?: { create?: boolean }): Promise<WorkspaceFileHandleLike>;
  removeEntry(name: string): Promise<void>;
  keys(): AsyncIterableIterator<string>;
}

export interface WorkspaceFileHandleLike {
  getFile(): Promise<{ arrayBuffer(): Promise<ArrayBuffer>; size: number }>;
  createWritable(): Promise<{
    write(data: BufferSource): Promise<void>;
    close(): Promise<void>;
  }>;
}

/** 可恢复的一份镜像。 */
export interface RecoverableEntry {
  readonly name: string;
  readonly bytes: Uint8Array;
  /** 镜像时间（ISO 8601）。 */
  readonly savedAt: string;
  /** 数据大小与 meta 记录一致。false 表示崩溃恰好发生在两个文件之间。 */
  readonly intact: boolean;
}

interface WorkspaceMeta {
  readonly name: string;
  readonly bytes: number;
  readonly savedAt: string;
}

const DATA_SUFFIX = '.bin';
const META_SUFFIX = '.meta.json';

/** 文档名 → 文件名键。base64url：无 `/`、无 `..`、可含任意 Unicode 原名。 */
function nameKey(name: string): string {
  const utf8 = new TextEncoder().encode(name);
  let binary = '';
  for (const byte of utf8) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

export class OpfsWorkspace implements FileGateway {
  readonly kind = 'opfs-workspace' as const;
  /** 镜像天然是原地覆盖。 */
  readonly canOverwrite = true;

  constructor(
    private readonly dir: WorkspaceDirLike,
    private readonly now: () => string = () => new Date().toISOString(),
  ) {}

  /**
   * 打开（必要时创建）工作区目录。OPFS 不可用（Safari 隐私模式等）时返回 null——
   * 没有崩溃恢复不该阻止编辑器启动，只是少了一层保险，UI 应提示。
   */
  static async open(
    directory = 'photosmith-workspace',
    env: {
      storage?: { getDirectory?: () => Promise<unknown> } | undefined;
    } = globalThis.navigator ?? {},
  ): Promise<OpfsWorkspace | null> {
    const getDirectory = env.storage?.getDirectory;
    if (typeof getDirectory !== 'function') return null;
    try {
      const root = (await getDirectory.call(env.storage)) as {
        getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<unknown>;
      };
      const dir = await root.getDirectoryHandle(directory, { create: true });
      return new OpfsWorkspace(dir as WorkspaceDirLike);
    } catch {
      return null; // 配额、隐私模式……都是预期内的"没有"
    }
  }

  async save(
    payload: SavePayload,
    _handle?: FileHandleRef | null,
    options?: GatewayCallOptions,
  ): Promise<FileHandleRef | null> {
    throwIfAborted(options?.signal);
    const key = nameKey(payload.name);

    // 先数据后 meta，见文件头的崩溃安全约定
    await this.#writeFile(`${key}${DATA_SUFFIX}`, payload.bytes);
    throwIfAborted(options?.signal);
    const meta: WorkspaceMeta = {
      name: payload.name,
      bytes: payload.bytes.length,
      savedAt: this.now(),
    };
    await this.#writeFile(
      `${key}${META_SUFFIX}`,
      new TextEncoder().encode(JSON.stringify(meta, null, 2)),
    );
    return { gateway: this.kind, name: payload.name };
  }

  async open(_accept: readonly string[], options?: GatewayCallOptions): Promise<OpenedFile[]> {
    throwIfAborted(options?.signal);
    const entries = await this.listRecoverable(options);
    return entries.map((entry) => ({
      name: entry.name,
      bytes: entry.bytes,
      handle: { gateway: this.kind, name: entry.name },
    }));
  }

  /** 枚举全部可恢复的镜像。崩溃后重启时调用。 */
  async listRecoverable(options?: GatewayCallOptions): Promise<RecoverableEntry[]> {
    const found: RecoverableEntry[] = [];
    for await (const fileName of this.dir.keys()) {
      throwIfAborted(options?.signal);
      if (!fileName.endsWith(META_SUFFIX)) continue;
      const key = fileName.slice(0, -META_SUFFIX.length);

      const meta = await this.#readMeta(fileName);
      if (meta === null) continue; // meta 都读不出来的条目直接跳过——没有可信的名字与大小
      const data = await this.#readFile(`${key}${DATA_SUFFIX}`);
      if (data === null) continue; // 只有 meta 没有数据：上次 discard 到一半，无可恢复
      found.push({
        name: meta.name,
        bytes: data,
        savedAt: meta.savedAt,
        intact: data.length === meta.bytes,
      });
    }
    return found;
  }

  /** 正常保存/关闭后丢弃镜像，别让恢复列表长期堆积。 */
  async discard(name: string): Promise<void> {
    const key = nameKey(name);
    // 先删 meta：枚举以 meta 为准，meta 没了这份镜像就"不存在"了，
    // 之后数据文件删失败也只是漏了几个字节的垃圾，不会出现幽灵条目
    await this.#removeQuiet(`${key}${META_SUFFIX}`);
    await this.#removeQuiet(`${key}${DATA_SUFFIX}`);
  }

  async #writeFile(fileName: string, bytes: Uint8Array): Promise<void> {
    const handle = await this.dir.getFileHandle(fileName, { create: true });
    const writable = await handle.createWritable();
    await writable.write(bytes as BufferSource);
    await writable.close();
  }

  async #readFile(fileName: string): Promise<Uint8Array | null> {
    try {
      const handle = await this.dir.getFileHandle(fileName);
      return new Uint8Array(await (await handle.getFile()).arrayBuffer());
    } catch {
      return null;
    }
  }

  async #readMeta(fileName: string): Promise<WorkspaceMeta | null> {
    const bytes = await this.#readFile(fileName);
    if (bytes === null) return null;
    try {
      const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<WorkspaceMeta>;
      if (typeof parsed.name !== 'string' || typeof parsed.bytes !== 'number') return null;
      return {
        name: parsed.name,
        bytes: parsed.bytes,
        savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
      };
    } catch {
      return null; // 写坏的 meta 等同没有
    }
  }

  async #removeQuiet(fileName: string): Promise<void> {
    try {
      await this.dir.removeEntry(fileName);
    } catch {
      /* 不存在即视为已删除 */
    }
  }
}
